import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, filter, take } from 'rxjs';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { selectUser } from 'src/app/core/store/selectors';
import { isPresent } from 'src/app/core/utils/isPresent';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { WorkspaceDetailsDto, WorkspaceDocumentDto } from '../../models/workspaces.model';
import { WorkspacesActions } from '../../store/actions';
import {
  selectAddingWorkspaceDocuments,
  selectWorkspaceDetails,
  selectWorkspaceDetailsLoading,
} from '../../store/selectors';
import { AddWorkspaceDocumentsModalComponent } from '../modals/add-workspace-documents-modal/add-workspace-documents-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-workspace-details',
  templateUrl: './workspace-details.component.html',
  styleUrl: './workspace-details.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class WorkspaceDetailsComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public workspaceId: string | null = null;
  public dataRecieved: boolean = false;
  public isWorkspaceDetailsLoading$ = this.store$.select(selectWorkspaceDetailsLoading);
  public isAddingWorkspaceDocuments$ = this.store$.select(selectAddingWorkspaceDocuments);
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public canWriteDocuments = false;
  private canWriteWorkspaces = false;
  private canWriteFileUpload = false;

  constructor(
    private readonly store$: Store,
    private titleService: Title,
    private bsModalService: BsModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(untilDestroyed(this)).subscribe((params) => {
      this.workspaceId = params.get('id');
      this.store$.dispatch(WorkspacesActions.getWorkspaceDetails({ id: this.workspaceId! }));
      this.cdr.markForCheck();
    });

    this.waitUserReadyAndStart();
  }

  waitUserReadyAndStart(): void {
    this.store$
      .select(selectUser)
      .pipe(untilDestroyed(this), filter(isPresent), take(1))
      .subscribe((user) => {
        this.startInitialization();
      });
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails).pipe(filter(isPresent)),
      this.permissionService.canWrite$(PermissionResource.Workspaces),
      this.permissionService.canWrite$(PermissionResource.Documents),
      this.permissionService.canWrite$(PermissionResource.FileUpload),
    ])
      .pipe(untilDestroyed(this), filter(isPresent))
      .subscribe(([data, canWriteWorkspaces, canWriteDocuments, canWriteFileUpload]) => {
        this.workspaceDetails = data;
        this.canWriteWorkspaces = canWriteWorkspaces;
        this.canWriteDocuments = canWriteDocuments;
        this.canWriteFileUpload = canWriteFileUpload;
        this.cdr.markForCheck();
      });

    this.titleService.setTitle('AutoLex | Workspace details');
  }

  addDocuments() {
    if (!this.canAddDocumentsToWorkspace) {
      return;
    }

    this.bsModalService.show(AddWorkspaceDocumentsModalComponent, {
      initialState: {},
      class: 'modal-xl',
    });
  }

  goToWorkspaces(): void {
    this.router.navigate(['/workspaces']);
  }

  get workspaceDocuments(): WorkspaceDocumentDto[] {
    return this.workspaceDetails?.documents || [];
  }

  get canAddDocumentsToWorkspace(): boolean {
    return this.canWriteWorkspaces && this.canWriteDocuments && this.canWriteFileUpload;
  }
}
